/**
 * Componente para configuração avançada de valores em condições
 * Permite múltiplos valores aceitos, opções de normalização e seleção de variáveis
 * 
 * RELEASE: 2.1.0 - 2024-12-30 - Usa VariableInsertButton padrão do sistema
 */

import { useState, KeyboardEvent } from 'react';
import { X, Plus, Lightbulb, Variable } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { VariableInsertButton } from '../VariableInsertButton';
import { cn } from '@/lib/utils';

/**
 * Opções de correspondência para normalização de valores
 */
export interface MatchOptions {
  ignoreCase: boolean;      // Ignorar maiúsculas/minúsculas
  ignoreAccents: boolean;   // Ignorar acentos (café = cafe)
  trimSpaces: boolean;      // Remover espaços extras
}

interface ValueConfigInputProps {
  values: string[];
  matchOptions: MatchOptions;
  onValuesChange: (values: string[]) => void;
  onMatchOptionsChange: (options: MatchOptions) => void;
  placeholder?: string;
  disabled?: boolean;
  tenantId?: string;           // ID do tenant para buscar variáveis
  allowVariables?: boolean;    // Permitir seleção de variáveis
}

/**
 * Sugestões de variações comuns para palavras frequentes
 */
const COMMON_VARIATIONS: Record<string, string[]> = {
  'sim': ['s', 'yes', 'yeah', 'si', 'positivo', 'ok', 'claro'],
  'não': ['nao', 'n', 'no', 'nope', 'negativo'],
  'ok': ['okay', 'blz', 'beleza', 'certo', 'entendi'],
  'oi': ['olá', 'ola', 'hey', 'hi', 'hello', 'eae', 'eai'],
  'tchau': ['adeus', 'bye', 'flw', 'falou', 'até', 'ate'],
};

/**
 * Verifica se um valor é uma referência a variável
 */
const isVariableReference = (value: string): boolean => {
  return value.startsWith('{{') && value.endsWith('}}');
};

/**
 * Extrai o nome da variável de uma referência
 */
const extractVariableName = (value: string): string => {
  return value.slice(2, -2).trim();
};

export function ValueConfigInput({
  values,
  matchOptions,
  onValuesChange,
  onMatchOptionsChange,
  placeholder = 'Digite um valor e pressione Enter',
  disabled = false,
  tenantId,
  allowVariables = false,
}: ValueConfigInputProps) {
  const [inputValue, setInputValue] = useState('');

  /**
   * Adiciona um novo valor à lista
   */
  const addValue = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && !values.includes(trimmed)) {
      onValuesChange([...values, trimmed]);
    }
    setInputValue('');
  };

  /**
   * Adiciona uma variável como valor (callback do VariableInsertButton)
   */
  const addVariableValue = (variable: { name: string }) => {
    const variableRef = `{{${variable.name}}}`;
    if (!values.includes(variableRef)) {
      onValuesChange([...values, variableRef]);
    }
  };

  /**
   * Remove um valor da lista
   */
  const removeValue = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onValuesChange(newValues);
  };

  /**
   * Handler para tecla Enter
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue(inputValue);
    }
  };

  /**
   * Adiciona variações comuns de uma palavra
   */
  const addCommonVariations = (baseWord: string) => {
    const variations = COMMON_VARIATIONS[baseWord.toLowerCase()];
    if (variations) {
      const newValues = [...values];
      variations.forEach(v => {
        if (!newValues.includes(v)) {
          newValues.push(v);
        }
      });
      onValuesChange(newValues);
    }
  };

  /**
   * Verifica se existe sugestão para algum valor
   */
  const hasSuggestions = values.some(v => 
    !isVariableReference(v) && COMMON_VARIATIONS[v.toLowerCase()] !== undefined
  );

  return (
    <div className="space-y-4">
      {/* Lista de valores como tags */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Valores aceitos</Label>
        
        <div className="flex flex-wrap gap-2 min-h-[38px] p-2 border rounded-md bg-background">
          {values.map((value, index) => {
            const isVariable = isVariableReference(value);
            const displayValue = isVariable ? extractVariableName(value) : value;
            
            return (
              <Badge 
                key={index} 
                variant={isVariable ? "default" : "secondary"}
                className={cn(
                  "flex items-center gap-1 px-2 py-1",
                  isVariable && "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700"
                )}
              >
                {isVariable && <Variable className="h-3 w-3" />}
                <span className="text-sm">{displayValue}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeValue(index)}
                    className="ml-1 hover:text-destructive transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            );
          })}
          
          {!disabled && (
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={values.length === 0 ? placeholder : 'Adicionar...'}
              className="flex-1 min-w-[120px] border-0 shadow-none focus-visible:ring-0 p-0 h-6"
              disabled={disabled}
            />
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Botão adicionar valor digitado */}
          {inputValue && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addValue(inputValue)}
              disabled={disabled}
            >
              <Plus className="h-3 w-3 mr-1" />
              Adicionar "{inputValue}"
            </Button>
          )}

          {/* Botão inserir variável - usa componente padrão do sistema */}
          {allowVariables && tenantId && (
            <VariableInsertButton
              tenantId={tenantId}
              onSelectVariable={addVariableValue}
              variant="button"
              buttonLabel="Inserir Variável"
            />
          )}

          {/* Sugestões de variações comuns */}
          {hasSuggestions && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      values.forEach(v => {
                        if (!isVariableReference(v) && COMMON_VARIATIONS[v.toLowerCase()]) {
                          addCommonVariations(v);
                        }
                      });
                    }}
                    disabled={disabled}
                    className="text-muted-foreground"
                  >
                    <Lightbulb className="h-3 w-3 mr-1" />
                    Adicionar variações comuns
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Adiciona variações como "s", "yes", "ok" automaticamente</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Opções de correspondência */}
      <div className="space-y-3 pt-2 border-t">
        <Label className="text-sm font-medium">Opções de correspondência</Label>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="ignoreCase"
              checked={matchOptions.ignoreCase}
              onCheckedChange={(checked) => 
                onMatchOptionsChange({ ...matchOptions, ignoreCase: checked === true })
              }
              disabled={disabled}
            />
            <label
              htmlFor="ignoreCase"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Ignorar maiúsculas/minúsculas (Sim = sim = SIM)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="ignoreAccents"
              checked={matchOptions.ignoreAccents}
              onCheckedChange={(checked) => 
                onMatchOptionsChange({ ...matchOptions, ignoreAccents: checked === true })
              }
              disabled={disabled}
            />
            <label
              htmlFor="ignoreAccents"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Ignorar acentos (café = cafe, não = nao)
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="trimSpaces"
              checked={matchOptions.trimSpaces}
              onCheckedChange={(checked) => 
                onMatchOptionsChange({ ...matchOptions, trimSpaces: checked === true })
              }
              disabled={disabled}
            />
            <label
              htmlFor="trimSpaces"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Ignorar espaços extras (" sim " = "sim")
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
